import {
  AnimationConfig,
  AnimationPreset,
  AnimationEasing,
  DEFAULT_ANIMATION_CONFIG,
} from '../types/animation';

/**
 * Validates and clamps animation values to prevent invalid or extreme inputs.
 */
export function validateAnimationConfig(config?: Partial<AnimationConfig> | null): AnimationConfig {
  if (!config) return { ...DEFAULT_ANIMATION_CONFIG };

  const clamp = (val: number, min: number, max: number, fallback: number) => {
    if (typeof val !== 'number' || isNaN(val)) return fallback;
    return Math.max(min, Math.min(max, val));
  };

  const validEasings: AnimationEasing[] = ['ease', 'ease-in', 'ease-out', 'ease-in-out', 'linear'];
  const validateEasing = (e?: any): AnimationEasing => {
    return validEasings.includes(e) ? e : 'ease-out';
  };

  const entrance = config.entrance || DEFAULT_ANIMATION_CONFIG.entrance!;
  const scroll = config.scroll || DEFAULT_ANIMATION_CONFIG.scroll!;
  const hover = config.hover || DEFAULT_ANIMATION_CONFIG.hover!;

  return {
    enabled: Boolean(config.enabled),
    entrance: {
      type: entrance.type || 'fade-up',
      duration: clamp(entrance.duration, 100, 2000, 700),
      delay: clamp(entrance.delay, 0, 1000, 0),
      easing: validateEasing(entrance.easing),
      trigger: entrance.trigger === 'on-load' ? 'on-load' : 'on-scroll',
    },
    scroll: {
      enabled: scroll.enabled !== false,
      type: scroll.type || 'fade-up',
      duration: clamp(scroll.duration, 100, 2000, 700),
      delay: clamp(scroll.delay, 0, 1000, 100),
      threshold: clamp(scroll.threshold, 0.05, 0.5, 0.15),
      once: scroll.once !== false,
      repeat: Boolean(scroll.repeat),
    },
    hover: {
      enabled: Boolean(hover.enabled),
      type: hover.type || 'lift',
      duration: clamp(hover.duration, 100, 1000, 250),
    },
    staggerChildren: clamp(config.staggerChildren ?? 100, 0, 1000, 100),
    repeat: Boolean(config.repeat),
    once: config.once !== false,
    mobileEnabled: config.mobileEnabled !== false,
    tabletEnabled: config.tabletEnabled !== false,
    desktopEnabled: config.desktopEnabled !== false,
    mobileOverrides: config.mobileOverrides,
    tabletOverrides: config.tabletOverrides,
    desktopOverrides: config.desktopOverrides,
  };
}

/**
 * Maps an animation preset to CSS keyframes and initial transform/opacity values.
 */
export function getPresetCss(preset: AnimationPreset): {
  keyframeName: string;
  initialTransform: string;
  initialOpacity: number;
} {
  switch (preset) {
    case 'fade':
      return { keyframeName: 'adiosoftFade', initialTransform: 'none', initialOpacity: 0 };
    case 'fade-up':
      return { keyframeName: 'adiosoftFadeUp', initialTransform: 'translateY(32px)', initialOpacity: 0 };
    case 'fade-down':
      return { keyframeName: 'adiosoftFadeDown', initialTransform: 'translateY(-32px)', initialOpacity: 0 };
    case 'fade-left':
      return { keyframeName: 'adiosoftFadeLeft', initialTransform: 'translateX(-32px)', initialOpacity: 0 };
    case 'fade-right':
      return { keyframeName: 'adiosoftFadeRight', initialTransform: 'translateX(32px)', initialOpacity: 0 };
    case 'zoom-in':
      return { keyframeName: 'adiosoftZoomIn', initialTransform: 'scale(0.92)', initialOpacity: 0 };
    case 'zoom-out':
      return { keyframeName: 'adiosoftZoomOut', initialTransform: 'scale(1.08)', initialOpacity: 0 };
    case 'slide-up':
      return { keyframeName: 'adiosoftSlideUp', initialTransform: 'translateY(50px)', initialOpacity: 0 };
    case 'slide-down':
      return { keyframeName: 'adiosoftSlideDown', initialTransform: 'translateY(-50px)', initialOpacity: 0 };
    case 'slide-left':
      return { keyframeName: 'adiosoftSlideLeft', initialTransform: 'translateX(-50px)', initialOpacity: 0 };
    case 'slide-right':
      return { keyframeName: 'adiosoftSlideRight', initialTransform: 'translateX(50px)', initialOpacity: 0 };
    case 'blur-in':
      return { keyframeName: 'adiosoftBlurIn', initialTransform: 'scale(0.96)', initialOpacity: 0 };
    case 'text-fade':
      return { keyframeName: 'adiosoftFade', initialTransform: 'none', initialOpacity: 0 };
    case 'text-slide-up':
      return { keyframeName: 'adiosoftFadeUp', initialTransform: 'translateY(24px)', initialOpacity: 0 };
    case 'text-reveal':
      return { keyframeName: 'adiosoftReveal', initialTransform: 'translateY(16px)', initialOpacity: 0 };
    case 'text-scale':
      return { keyframeName: 'adiosoftZoomIn', initialTransform: 'scale(0.95)', initialOpacity: 0 };
    case 'image-fade':
      return { keyframeName: 'adiosoftFade', initialTransform: 'none', initialOpacity: 0 };
    case 'image-zoom':
      return { keyframeName: 'adiosoftZoomIn', initialTransform: 'scale(0.92)', initialOpacity: 0 };
    case 'image-slide':
      return { keyframeName: 'adiosoftFadeUp', initialTransform: 'translateY(40px)', initialOpacity: 0 };
    case 'button-fade':
      return { keyframeName: 'adiosoftFade', initialTransform: 'none', initialOpacity: 0 };
    case 'button-lift':
      return { keyframeName: 'adiosoftFadeUp', initialTransform: 'translateY(16px)', initialOpacity: 0 };
    case 'button-scale':
      return { keyframeName: 'adiosoftZoomIn', initialTransform: 'scale(0.9)', initialOpacity: 0 };
    case 'button-glow':
      return { keyframeName: 'adiosoftFade', initialTransform: 'none', initialOpacity: 0 };
    case 'card-fade':
      return { keyframeName: 'adiosoftFade', initialTransform: 'none', initialOpacity: 0 };
    case 'card-lift':
      return { keyframeName: 'adiosoftFadeUp', initialTransform: 'translateY(28px)', initialOpacity: 0 };
    case 'card-scale':
      return { keyframeName: 'adiosoftZoomIn', initialTransform: 'scale(0.94)', initialOpacity: 0 };
    case 'none':
    default:
      return { keyframeName: 'none', initialTransform: 'none', initialOpacity: 1 };
  }
}

/**
 * Returns comprehensive CSS keyframes definitions for the static stylesheet and export.
 */
export function getAnimationKeyframesCss(): string {
  return `
/* Adiosoft Animation System 2.0 Keyframes */
@keyframes adiosoftFade {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes adiosoftFadeUp {
  from {
    opacity: 0;
    transform: translate3d(0, 32px, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

@keyframes adiosoftFadeDown {
  from {
    opacity: 0;
    transform: translate3d(0, -32px, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

@keyframes adiosoftFadeLeft {
  from {
    opacity: 0;
    transform: translate3d(-32px, 0, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

@keyframes adiosoftFadeRight {
  from {
    opacity: 0;
    transform: translate3d(32px, 0, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

@keyframes adiosoftZoomIn {
  from {
    opacity: 0;
    transform: scale3d(0.92, 0.92, 1);
  }
  to {
    opacity: 1;
    transform: scale3d(1, 1, 1);
  }
}

@keyframes adiosoftZoomOut {
  from {
    opacity: 0;
    transform: scale3d(1.08, 1.08, 1);
  }
  to {
    opacity: 1;
    transform: scale3d(1, 1, 1);
  }
}

@keyframes adiosoftSlideUp {
  from {
    opacity: 0;
    transform: translate3d(0, 50px, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

@keyframes adiosoftSlideDown {
  from {
    opacity: 0;
    transform: translate3d(0, -50px, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

@keyframes adiosoftSlideLeft {
  from {
    opacity: 0;
    transform: translate3d(-50px, 0, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

@keyframes adiosoftSlideRight {
  from {
    opacity: 0;
    transform: translate3d(50px, 0, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

@keyframes adiosoftBlurIn {
  from {
    opacity: 0;
    filter: blur(8px);
    transform: scale3d(0.96, 0.96, 1);
  }
  to {
    opacity: 1;
    filter: blur(0);
    transform: scale3d(1, 1, 1);
  }
}

@keyframes adiosoftReveal {
  from {
    opacity: 0;
    transform: translate3d(0, 16px, 0);
    clip-path: inset(100% 0 0 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
    clip-path: inset(0 0 0 0);
  }
}

/* Hover Presets */
.anim-hover-lift {
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.25s ease;
}
@media (hover: hover) {
  .anim-hover-lift:hover {
    transform: translate3d(0, -6px, 0);
    box-shadow: 0 16px 32px -8px rgba(0, 0, 0, 0.15);
  }
}

.anim-hover-scale {
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}
@media (hover: hover) {
  .anim-hover-scale:hover {
    transform: scale3d(1.03, 1.03, 1);
  }
}

.anim-hover-glow {
  transition: box-shadow 0.3s ease, transform 0.25s ease;
}
@media (hover: hover) {
  .anim-hover-glow:hover {
    box-shadow: 0 0 24px 4px rgba(37, 99, 235, 0.35);
    transform: translate3d(0, -2px, 0);
  }
}

/* Accessibility: Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  *, ::before, ::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
  .adiosoft-animating {
    opacity: 1 !important;
    transform: none !important;
    filter: none !important;
    clip-path: none !important;
  }
}
`;
}
